<template>
  <section class="banner" :class="{ 'is-borderless': isBorderless }">
    <MessageView
      :type="type"
      aria-close-label="Close message"
      :closable="showClose"
      @close="onClose"
    >
      <template #header>
        <div class="columns is-mobile is-vcentered banner-layout">
          <div v-if="showIcon" class="column is-narrow m-t-3 banner-icon">
            <slot name="icon">
              <span class="has-text-warning"><i :class="`cls cls-${customIcon ?? icon}`"></i></span>
            </slot>
          </div>
          <div class="column banner-title" :class="customTitleClass">
            <slot name="title">
              <span class="has-text-black has-text-weight-normal" :class="titleTextSize">
                {{ title }}
              </span>
            </slot>
          </div>
          <div
            v-if="$slots['action-button']"
            class="column is-narrow-tablet is-full-mobile banner-action m-r-10"
          >
            <slot name="action-button"></slot>
          </div>
        </div>
      </template>
    </MessageView>
  </section>
</template>

<script>
import { BannerMixin } from '@/modules/core/ui/mixins/BannerMixin';
import { BannerType } from '@/modules/core/ui/constants/BannerType';
import MessageView from '@/modules/core/ui/components/wrappers/MessageView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'Banner',
  emits: ['on-close'],
  slotsDefinition: {
    icon: {},
    title: {},
    actionButton: {}
  },
  mixins: [BannerMixin],
  components: { MessageView },
  computed: {
    showIcon() {
      if (!this.hasIcon) {
        return false;
      }

      return this.icon !== '' || this.customIcon !== '';
    },
    icon() {
      switch (this.type) {
        case BannerType.IS_SUCCESS:
        case BannerType.IS_DISABLED:
          return 'check-circle-1';
        case BannerType.IS_DANGER:
          return '';
        case BannerType.IS_WARNING:
          return 'alert-circle';
        default:
          return '';
      }
    },
    customTitleClass() {
      return !this.hasIcon ? 'p-l-10' : 'p-l-2';
    }
  },
  methods: {
    onClose() {
      this.$emit('on-close');
    }
  }
};
</script>

<style scoped>
.banner :deep(.message) {
  box-shadow: var(--ce-card-box-shadow);
}

.banner :deep(.message.is-primary .message-header) {
  border: var(--ce-primary) var(--ce-1px) solid;
  border-width: var(--ce-1px) var(--ce-1px) var(--ce-1px) var(--ce-4px);
}

.banner :deep(.message.is-primary .message-header i) {
  color: var(--ce-primary);
}

.banner :deep(.message.is-success .message-header) {
  border: var(--ce-success) var(--ce-1px) solid;
  border-width: var(--ce-1px) var(--ce-1px) var(--ce-1px) var(--ce-4px);
}

.banner :deep(.message.is-success .message-header i) {
  color: var(--ce-success) !important;
}

.banner :deep(.message.is-warning .message-header) {
  border: var(--ce-warning) var(--ce-1px) solid;
  border-width: var(--ce-1px) var(--ce-1px) var(--ce-1px) var(--ce-4px);
}

.banner :deep(.message.is-warning .message-header i) {
  color: var(--ce-warning) !important;
}

.banner :deep(.message.is-tertiary .message-header) {
  border: var(--ce-tertiary) var(--ce-1px) solid;
  border-width: var(--ce-1px) var(--ce-1px) var(--ce-1px) var(--ce-4px);
}

/* Scoped to .banner-icon so icons inside the title slot keep their own colour. */
.banner :deep(.message.is-tertiary .message-header .banner-icon i) {
  color: var(--ce-tertiary) !important;
}

.banner :deep(.message.is-danger .message-header) {
  border: var(--ce-danger) var(--ce-1px) solid;
  border-width: var(--ce-1px) var(--ce-1px) var(--ce-1px) var(--ce-4px);
}

.banner :deep(.message.is-danger .message-header i) {
  color: var(--ce-danger) !important;
}

.banner :deep(.message.is-disabled .message-header) {
  border: var(--ce-grey) var(--ce-1px) solid;
  border-width: var(--ce-1px) var(--ce-1px) var(--ce-1px) var(--ce-4px);
  opacity: 0.6;
}

.banner :deep(.message.is-disabled .message-header i) {
  color: var(--ce-grey) !important;
}

.banner :deep(.message.is-disabled .message-header span) {
  color: var(--ce-grey) !important;
}

.banner :deep(.message .message-header) {
  background-color: var(--ce-white);
  border-radius: var(--ce-5px);
}

.banner :deep(.message .message-header > div) {
  width: 100%;
}

.banner.is-borderless :deep(> .message) {
  box-shadow: unset;
  background-color: unset;
}

.banner.is-borderless :deep(> .message > .message-header) {
  border-radius: var(--ce-3px) var(--ce-8px) var(--ce-8px) var(--ce-3px) !important;
  border-width: 0 0 0 var(--ce-3px) !important;
  background-color: var(--ce-secondary-bg-color);
}

.banner :deep(.message .message-body) {
  display: none;
}

@media screen and (max-width: CE_MOBILE) {
  .banner-layout {
    align-items: flex-start !important;
    flex-wrap: wrap;
  }

  .banner-action {
    margin-right: 0;
    padding-left: var(--ce-35px);
    justify-content: flex-start;
    display: flex;
  }
}
</style>
